import React from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import DisabledFormSection from 'components/DisabledFormSection';
import { useFormConfig } from 'config/formConfig/hooks';
import Condition from 'modules/ConditionalSubform/Condition';
import {
  COMPANY_DETAILS,
  FINANCING_NEED,
  INDUSTRY__CRAFT,
  PURPOSE_KIND,
  PURPOSE_KIND__KFINANZ,
  USER_PROFILE_INDUSTRY,
  USER_PROFILE_TURNOVER_CLASS,
} from 'modules/Inquiry/Form/formFields';
import { useCompanyDetailsSectionValidators } from 'modules/Inquiry/Form/Validations/default/useCompanyDetailsSectionValidators';
import { InquiryType } from 'modules/Inquiry/Inquiry.type';
import { useInquiryNavigation } from 'modules/Inquiry/inquiryNavigation/useInquiryNavigation';
import InquiryFormNew from 'modules/InquiryFormNew/InquiryFormNew';
import { NUMBER_OF_LIFTS } from 'onlinefactoring/formFields';
import CompanyDetailsHeader from 'pages/inquiryFlow/CompanyDetails/CompanyDetailsHeader/CompanyDetailsHeader';
import Company, {
  validateCompanyNameFields,
} from 'pages/inquiryFlow/CompanyDetails/sections/Company';
import { useInquiryProcessLaneCheck } from 'pages/inquiryFlow/useProcessLaneCheck';
import CompletingCompanyDetails from 'pages/inquiryFlow/UserProfile/sections/CompletingCompanyDetails';
import { setCreatedInquiryId } from 'store/progress/actions';
import { getStoredValueSelector } from 'store/progress/selectors';
import { getExpirationDate, getRefreshToken, setToken } from 'utils/auth';
import { useTranslations } from 'utils/hooks/useTranslations';
import { isLoggedInUser } from 'utils/user/conditionals';

import { getTurnoverClass } from './getTurnoverClass';
import TermsAndConditions from './sections/TermsAndConditions';
import { useSendPartialSpecializedInquiry } from '../Rentenbank/useSendSpecializedInquiry';

const CompanyDetails = () => {
  const getSectionValidators = useCompanyDetailsSectionValidators();
  const isLoggedIn = isLoggedInUser();
  const t = useTranslations();
  const { pathToNextStep } = useInquiryNavigation(COMPANY_DETAILS);
  const { selectedInquiryType } = useFormConfig();
  const refreshToken = getRefreshToken();
  const history = useHistory();
  const { mutation } = useInquiryProcessLaneCheck();
  const sendPartialSpecializedInquiry = useSendPartialSpecializedInquiry();
  const dispatch = useDispatch();

  const handlePartialSpecializedInquiry = async () => {
    const res = await sendPartialSpecializedInquiry();
    if (!res.error) {
      const inquiryId = res.payload.data.data.id;
      if (!refreshToken) {
        const token = res.payload.headers['temp-access-token'];
        const refreshToken = res.payload.headers['temp-refresh-token'];
        const expiresIn = res.payload.headers['temp-expires-in'];
        const createdAt = res.payload.headers['temp-created-at'];
        const expiresAt = getExpirationDate(Number(createdAt), Number(expiresIn));

        setToken({ token, refreshToken, expiresAt, scope: 'customer' });
      }

      dispatch(setCreatedInquiryId(inquiryId));
      await mutation.mutateAsync({ id: inquiryId });

      if (pathToNextStep) history.push(pathToNextStep);
    }
  };

  const handleSubmit = async () => {
    switch (selectedInquiryType) {
      case InquiryType.onlinefactoring:
      case InquiryType.bfsService:
        await handlePartialSpecializedInquiry();
        break;
      default:
        if (pathToNextStep) {
          history.push(pathToNextStep);
        }
    }
  };
  const selectedNumberOfLifts = useSelector(
    getStoredValueSelector([FINANCING_NEED, NUMBER_OF_LIFTS]),
  );
  const selectedPurposeKind = useSelector(getStoredValueSelector([FINANCING_NEED, PURPOSE_KIND]));
  const isKfinanzPurpose = selectedPurposeKind === PURPOSE_KIND__KFINANZ;

  const initialValues = isKfinanzPurpose
    ? {
        [USER_PROFILE_INDUSTRY]: INDUSTRY__CRAFT,
        [USER_PROFILE_TURNOVER_CLASS]:
          selectedNumberOfLifts && getTurnoverClass(selectedNumberOfLifts),
      }
    : undefined;

  return (
    <InquiryFormNew
      headerComp={<CompanyDetailsHeader />}
      currentStepName={COMPANY_DETAILS}
      getSectionValidators={getSectionValidators}
      isLoading={mutation.isLoading}
      onSubmit={handleSubmit}
      initialValues={initialValues}
    >
      {() => (
        <>
          <Company tooltip={t('pages.userProfile.sections.completingContactData.tooltip')} />
          <Condition
            condition={({ form }: { form: any }) => validateCompanyNameFields(isLoggedIn)(form)}
            onFail={
              <DisabledFormSection
                sectionNumber={2}
                placeholder={t(
                  'pages.userProfile.sections.completingContactData.pleaseSelectCompany',
                )}
              />
            }
          >
            <CompletingCompanyDetails />
            {selectedInquiryType === InquiryType.onlinefactoring && (
              <TermsAndConditions sectionNumber={3} />
            )}
          </Condition>
        </>
      )}
    </InquiryFormNew>
  );
};

export default CompanyDetails;
